import React from "react";
import { type LucideIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { PendingMediaPlaceholder, type PendingItem } from "@/components/flowly/PendingMediaPlaceholder/PendingMediaPlaceholder";
import { FailedMediaPlaceholder } from "@/components/flowly/FailedMediaPlaceholder/FailedMediaPlaceholder";
import { EmptyState } from "@/components/flowly/infra/EmptyState/EmptyState";
import { SuggestIdeaBadge } from "./SuggestIdeaBadge";

interface CreationsGalleryProps {
  title?: string;
  galleryRef?: React.RefObject<HTMLDivElement>;
  pendingCount: number;
  pendingLabel: string;
  pendingItems: PendingItem[];
  pendingMediaType: "image" | "video";
  completingIds?: Set<string>;
  failedItems?: Array<{ id: string }>;
  failedLabel?: string;
  onClearFailed?: () => void;
  gridClassName?: string;
  className?: string;
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptySubtitle?: string;
  children: React.ReactNode;
}

export function CreationsGallery({
  title,
  galleryRef,
  pendingCount,
  pendingLabel,
  pendingItems,
  pendingMediaType,
  completingIds,
  failedItems,
  failedLabel,
  onClearFailed,
  gridClassName,
  className,
  emptyIcon,
  emptyTitle,
  emptySubtitle,
  children,
}: CreationsGalleryProps) {
  const { t } = useLanguage();
  const failedCount = failedItems?.length ?? 0;
  const isEmpty =
    React.Children.count(children) === 0 &&
    pendingCount === 0 &&
    failedCount === 0;

  const clearFailedButton = failedCount > 0 && onClearFailed ? (
    <button
      onClick={onClearFailed}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
    >
      <Trash2 className="w-3.5 h-3.5" />
      {t.gallery.clearFailed}
    </button>
  ) : null;

  return (
    <div className={cn("w-full", className)} ref={galleryRef} data-tour="creations-gallery">
      {title ? (
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 px-4 md:px-6">
          <h3 className="text-foreground font-bold text-start text-base md:text-lg">
            {title}
          </h3>
          <div className="flex items-center gap-2">
            {clearFailedButton}
            <SuggestIdeaBadge />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2 mb-2 px-4 md:px-6">
          {clearFailedButton}
          <SuggestIdeaBadge />
        </div>
      )}
      {isEmpty && emptyIcon && emptyTitle ? (
        <div className="border-t border-border">
          <EmptyState
            icon={emptyIcon}
            title={emptyTitle}
            subtitle={emptySubtitle}
            className="py-20"
          />
        </div>
      ) : (
        <div
          className={cn(
            "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 border-t border-border overflow-hidden",
            gridClassName,
          )}
        >
          <PendingMediaPlaceholder
            count={pendingCount}
            label={pendingLabel}
            items={pendingItems}
            mediaType={pendingMediaType}
            completingIds={completingIds}
          />
          {pendingCount === 0 && failedItems && failedLabel && (
            <FailedMediaPlaceholder items={failedItems} label={failedLabel} />
          )}
          {children}
        </div>
      )}
    </div>
  );
}
